import { Tabs, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';
import './PillTabList.css';

export type PillTabItem = {
  value: string;
  /** Icon or label content rendered inside the tab */
  children: ReactNode;
  'aria-label'?: string;
  title?: string;
  disabled?: boolean;
};

type TabsContextProps = {
  /** Controlled value – when provided the component operates in standalone mode (no Tabs context needed) */
  value?: undefined;
  onChange?: undefined;
};

type StandaloneProps = {
  /** Controlled value – operates in standalone mode (no Tabs context needed) */
  value: string;
  onChange: (value: string) => void;
};

type PillTabListProps = (TabsContextProps | StandaloneProps) & {
  items: PillTabItem[];
  /** Override width of the container. Defaults to auto (fits content). */
  width?: number | string;
  'aria-label'?: string;
};

/**
 * Pill-style tab list that matches the Hubs page design:
 * - Dark muted container with 3 px padding
 * - Rounded terracotta background on active tab
 * - Smooth opacity/color transitions
 *
 * **Tabs context mode** (default): renders `<Tabs.List>` + `<Tabs.Tab>`.
 * Must be inside a `<Tabs>` tree.
 *
 * **Standalone mode**: pass `value` + `onChange`. Renders a `role="group"`
 * container with plain buttons – no Tabs context required.
 */
export function PillTabList({ items, width, 'aria-label': ariaLabel, value, onChange }: PillTabListProps) {
  const style = width !== undefined ? { width } : undefined;

  // Standalone controlled mode
  if (value !== undefined && onChange !== undefined) {
    return (
      <div role="group" aria-label={ariaLabel} className="pill-tab-list" style={style}>
        {items.map((item) => (
          <UnstyledButton
            key={item.value}
            className="pill-tab-list__btn"
            data-active={item.value === value || undefined}
            aria-label={item['aria-label']}
            aria-pressed={item.value === value}
            title={item.title}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.value)}
          >
            {item.children}
          </UnstyledButton>
        ))}
      </div>
    );
  }

  // Tabs context mode
  return (
    <Tabs.List aria-label={ariaLabel} className="pill-tab-list" style={style}>
      {items.map((item) => (
        <Tabs.Tab
          key={item.value}
          value={item.value}
          aria-label={item['aria-label']}
          title={item.title}
          disabled={item.disabled}
        >
          {item.children}
        </Tabs.Tab>
      ))}
    </Tabs.List>
  );
}
